import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withoutForbiddenElevation } from '@application/data-access';
import {
  IAccountStatus, IChangePaswordRequest, IChangeTemporaryPassword, ICheckResetPasswordCode,
  IConfirmEmailAccount, ICreateAccount, ICreateAccountPassword,
  IFavoriteMenu,
  INewPassword,
  IResendEmailAccountConfirmation, IResendEmailLinkConfirmation, ISendEmailResetPassword,
  TCountryCodePhoneNumber,
  TGroup,
  TOperationsPeriodsDescriptions
} from '../entities/account.interface';

/**
 * Service responsible for handling all account-related HTTP requests
 * Provides methods for account creation, email confirmation, password management
 */
@Injectable({providedIn: 'root'})
export class AccountService {
  /** HTTP client for making API requests */
  public http = inject(HttpClient);

  /** Application configuration */
  private readonly appConfig = inject(APP_CONFIG);

  /** Application source identifier */
  private readonly appSource = this.appConfig.appSource;

  /** Base API URL */
  private readonly apiUrl = this.appConfig.apiUrl;

  /**
   * Checks the status of an account by email
   * @param email - The email address to check
   * @param appSource - Optional application source (Actuar or FacilFit)
   * @returns Observable with account status information
   */
  public checkAccountStatus(email: string): Observable<IAccountStatus> {
    let url = this.apiUrl.userService + '/Auth/CheckStatus?email=' + email;
    return this.http.get<IAccountStatus>(url);
  }
  
  public confirmEmailAndCreatePassword(payload: Partial<INewPassword>): Observable<any> {
    return this.http.post(`${this.apiUrl.userService }/Auth/ConfirmEmailAccountAndCreatePassword`, payload, {
      withCredentials: false
    })
  }

  /**
   * Creates a new user account
   * @param payload - Account creation data
   * @returns Observable with created account information
   */
  public createAccount(payload: Partial<ICreateAccount>): Observable<ICreateAccount> {
    return this.http.post<ICreateAccount>(
        this.apiUrl.userService + '/Auth/CreateAccount',
      { ...payload, AppSource: this.appSource }
    );
  }

  /**
   * Confirms user email address
   * @param payload - Email confirmation data
   * @returns Observable indicating success/failure
   */
  public confirmEmail(payload: IConfirmEmailAccount): Observable<boolean> {
    return this.http.post<boolean>(
        this.apiUrl.userService + '/Auth/ConfirmEmail',
      payload
    );
  }

  /**
   * Resets user password
   * @param payload - Password reset data
   * @returns Observable indicating success/failure
   */
  public resetPassword(payload: unknown): Observable<boolean> {
    return this.http.post<boolean>(
        this.apiUrl.userService + '/Password/ResetPassword',
      payload
    );
  }

  /**
   * Resends email confirmation code
   * @param payload - Email confirmation request data
   * @returns Observable indicating success/failure
   */
  public resendEmailCodeConfirmAccount(payload: IResendEmailAccountConfirmation): Observable<boolean> {
    return this.http.post<boolean>(
        this.apiUrl.userService + '/Auth/ResendEmailConfirmation',
      payload
    );
  }

  /**
   * Sends password reset email
   * @param payload - Password reset request data
   * @returns Observable indicating success/failure
   */
  public sendEmailResetPassword(payload: ISendEmailResetPassword): Observable<boolean> {
    return this.http.post<boolean>(
              this.apiUrl.userService +
        '/Password/SendEmailResetPassword',
      payload
    );
  }

  /**
   * Resends email link confirmation
   * @param payload - Email and the AppSource requesting the resend, so the
   * backend picks the matching email layout and return URL
   * @returns Observable indicating success/failure
   */
  public resendEmailLinkConfirmation(payload: IResendEmailLinkConfirmation): Observable<boolean> {
    return this.http
      .post<boolean>(
        this.apiUrl.userService + '/Verification/ResendEmailLinkConfirmation',
        payload,
        {
          withCredentials: false
        }
      );
  }

  /**
   * Verifies password reset code validity
   * @param payload - Code verification data
   * @returns Observable indicating if code is valid
   */
  public checkResetPasswordCode(payload: ICheckResetPasswordCode): Observable<boolean> {
    return this.http.post<boolean>(
              this.apiUrl.userService +
        '/Password/CheckResetPasswordCode',
      payload
    );
  }

  /**
   * Creates initial password for a new account
   * @param payload - Password creation data
   * @returns Observable indicating success/failure
   */
  public createAccountPassword(payload: ICreateAccountPassword): Observable<boolean> {
    return this.http.post<boolean>(
        this.apiUrl.userService +
       '/Password/CreatePassword',
      payload
    );
  }

  /**
   * Changes password for authenticated user
   * @param payload - Password change request data
   * @returns Observable indicating success/failure
   */
  public changeSelfUserPassword(payload: IChangePaswordRequest): Observable<boolean> {
    return this.http.post<boolean>(
        this.apiUrl.userService +
       '/Password/ChangePassword',
      payload,
      {
        withCredentials: true,
      }
    );
  }

  /**
   * Favorite a listed menu
   * @param menu - Menu to be favorite data
   * @returns Observable indicating success/failure
   */
  public favoriteMenus(menu: IFavoriteMenu[]): Observable<IFavoriteMenu[]> {  
    return this.http.post<IFavoriteMenu[]>(
         this.apiUrl.account_service +
        '/FavoriteMenus',
        menu,  
        { withCredentials: true }
    );
  }

  public getOperationsPeriodsDescriptions(): Observable<TOperationsPeriodsDescriptions[]> {
    return this.http.get<TOperationsPeriodsDescriptions[]>(`${this.apiUrl.account_service}/OperationPeriod/GetOperationsPeriodsDescriptions`, {
      withCredentials: true
    });
  }

   public checkCountryCodePhoneNumber(countryCode: string): Observable<TCountryCodePhoneNumber> {
    return this.http
      .get<TCountryCodePhoneNumber>(`${this.apiUrl.account_service}/SettingsOptions/GetCountryCodePhoneNumber?language=${countryCode}`, {
        withCredentials: true
      });
  }

  /**
   * Changes temporary password for user
   * @param payload - Temporary password change data
   * @returns Observable indicating success/failure
   */
  public changeTemporaryPassword(payload: IChangeTemporaryPassword): Observable<boolean> {
    return this.http.post<boolean>(
      this.apiUrl.userService + '/Password/ChangeTemporayPassword',
      payload
    );
  }

  public listGroups(): Observable<TGroup[]> {
    return this.http.request<TGroup[]>('OPTIONS', `${this.apiUrl.account_service}/Group`, {
      withCredentials: true,
    });
  }

  public getGroup(id: string): Observable<TGroup> {
    return this.http.get<TGroup>(`${this.apiUrl.account_service}/Group`, {
      params: { id },
      context: withoutForbiddenElevation(),
      withCredentials: true,
    });
  }

  public createGroup(payload: Partial<TGroup>): Observable<TGroup> {
    return this.http.post<TGroup>(`${this.apiUrl.account_service}/Group`, payload, {
      context: withoutForbiddenElevation(),
      withCredentials: true,
    });
  }

  public updateGroup(payload: Partial<TGroup>): Observable<TGroup> {
    return this.http.put<TGroup>(`${this.apiUrl.account_service}/Group`, payload, {
      context: withoutForbiddenElevation(),
      withCredentials: true,
    });
  }

  public deleteGroup(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.account_service}/Group`, {
      params: { id },
      context: withoutForbiddenElevation(),
      withCredentials: true,
    });
  }

  public getGroupByMemberTenantId(memberTenantId: string): Observable<TGroup> {
    return this.http.get<TGroup>(`${this.apiUrl.account_service}/Group/GetByMemberTenantId`, {
      params: { memberTenantId },
      withCredentials: true
    });
  }
}
